/**
 * Swiper is resolved from the importmap in snippets/scripts.liquid, which points
 * at the core build plus the three modules the sliders below actually enable.
 *
 * Two deliberate choices here, both for main-thread cost:
 *
 * 1. Core + `modules: [...]` instead of `swiper-bundle`. The bundle is a barrel
 *    that re-exports every module Swiper ships — zoom, pagination, virtual,
 *    thumbs, autoplay, all the `effect-*` — so importing it downloads and
 *    evaluates ~80 KB of JS across ~20 extra requests that no slider here uses.
 * 2. A dynamic `import()` behind a DOM check. This file is loaded on every page
 *    from the layout, but only two templates render a slider; without the guard
 *    every other page pays Swiper's parse and evaluation for nothing.
 */

const quickAddSliders = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('.quick-add-slider'));
const productPageSliders = /** @type {NodeListOf<HTMLElement>} */ (
  document.querySelectorAll('.custom-productpage-slider')
);

const KEYBOARD = { enabled: true, onlyInViewport: true };

if (quickAddSliders.length > 0 || productPageSliders.length > 0) {
  initSliders();
}

async function initSliders() {
  const [{ default: Swiper }, { default: Navigation }, { default: Keyboard }, { default: A11y }] = await Promise.all([
    import('swiper'),
    import('swiper/modules/navigation'),
    import('swiper/modules/keyboard'),
    import('swiper/modules/a11y'),
  ]);

  // Core Swiper silently ignores `navigation`, `keyboard` and `a11y` options
  // unless the matching modules are registered on the instance.
  const MODULES = [Navigation, Keyboard, A11y];

  // Iterate explicitly rather than handing Swiper a '.quick-add-slider' selector
  // string: each instance needs its own navigation elements so their per-slider
  // aria-labels can be read, instead of relying on global selector matching.
  quickAddSliders.forEach((sliderEl) => {
    const prevEl = sliderEl.querySelector('.swiper-button-prev');
    const nextEl = sliderEl.querySelector('.swiper-button-next');

    new Swiper(sliderEl, {
      modules: MODULES,
      slidesPerView: 1,
      navigation: { nextEl, prevEl },
      keyboard: KEYBOARD,
      a11y: a11yLabelsFromDom(prevEl, nextEl, sliderEl),
    });
  });

  productPageSliders.forEach((container) => {
    if (container.dataset.recommendationsPerformed === 'true') {
      requestAnimationFrame(() => initCustomProductPageSlider(Swiper, MODULES, container));
      return;
    }

    const observer = new MutationObserver(() => {
      if (container.dataset.recommendationsPerformed !== 'true') return;
      observer.disconnect();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => initCustomProductPageSlider(Swiper, MODULES, container))
      );
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['data-recommendations-performed'],
    });
  });
}

/**
 * Swiper's A11y module is enabled by default in swiper-bundle, but it ships
 * hardcoded English messages ("Previous slide", "Next slide") and writes them over
 * whatever aria-label is already on the navigation elements — silently discarding
 * the theme's translations. Read the Liquid-rendered labels back out of the DOM so
 * the active locale wins, and opt into the container role + arrow-key navigation,
 * neither of which Swiper turns on by itself.
 *
 * @param {Element | null} prevEl
 * @param {Element | null} nextEl
 * @param {HTMLElement} container - Carries the optional data-a11y-* labels.
 * @returns {Record<string, string>}
 */
function a11yLabelsFromDom(prevEl, nextEl, container) {
  /** @type {Record<string, string>} */
  const a11y = {};

  const prevMessage = prevEl?.getAttribute('aria-label');
  if (prevMessage) a11y.prevSlideMessage = prevMessage;

  const nextMessage = nextEl?.getAttribute('aria-label');
  if (nextMessage) a11y.nextSlideMessage = nextMessage;

  const { a11yRoledescription, a11yLabel } = container.dataset;
  if (a11yRoledescription) {
    a11y.containerRole = 'region';
    a11y.containerRoleDescriptionMessage = a11yRoledescription;
  }
  if (a11yLabel) a11y.containerMessage = a11yLabel;

  return a11y;
}

/**
 * @param {any} Swiper
 * @param {any[]} modules
 * @param {HTMLElement} container
 */
function initCustomProductPageSlider(Swiper, modules, container) {
  const swiperEl = /** @type {(HTMLElement & { swiper?: any }) | null} */ (container.querySelector('.cps__swiper'));
  if (!swiperEl) return;

  if (swiperEl.swiper) swiperEl.swiper.destroy(true, true);

  const prevEl = container.querySelector('.cps__nav-btn--prev');
  const nextEl = container.querySelector('.cps__nav-btn--next');

  new Swiper(swiperEl, {
    modules,
    slidesPerView: 1.5,
    spaceBetween: 12,
    navigation: { nextEl, prevEl },
    keyboard: KEYBOARD,
    a11y: a11yLabelsFromDom(prevEl, nextEl, container),
    breakpoints: {
      750: {
        slidesPerView: 3,
        spaceBetween: 16,
      },
      1100: {
        slidesPerView: 4,
        spaceBetween: 20,
      },
    },
  });
}
